import create from "zustand";

import {
    createUserWithEmailAndPassword,
    FacebookAuthProvider,
    linkWithCredential,
    linkWithPopup,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { readUserAccessFields } from '../utils/access';
import {
    FACEBOOK_SIGN_IN_CONFIG_ID,
    FACEBOOK_SIGN_IN_SCOPES,
} from '../utils/FB/facebookLoginConfig';



const defaultState = {
    user: null,
    profile: null,
    role: 'guest',
    clientIds: [],
    cameraIds: [],
    locationIds: [],
    isAccessLoading: false,
    accessError: '',
};

const useAuthStore = create((set) => ({
    ...defaultState,
    setUser: (user) => set({ user }),
    clearAccess: () => set({
        profile: null,
        role: 'guest',
        clientIds: [],
        cameraIds: [],
        locationIds: [],
        isAccessLoading: false,
        accessError: '',
    }),
    fetchUserAccess: async (user) => {
        const uid = typeof user === 'string' ? user : user?.uid;
        if (!uid) {
            set({
                profile: null,
                role: 'guest',
                clientIds: [],
                cameraIds: [],
                locationIds: [],
                isAccessLoading: false,
                accessError: '',
            });
            return null;
        }

        set({ isAccessLoading: true, accessError: '' });
        try {
            const userRef = doc(db, 'users', uid);
            const snapshot = await getDoc(userRef);

            let data = {};
            if (!snapshot.exists()) {
                data = { role: 'client', clientIds: [], cameraIds: [], locationIds: [] };
                await setDoc(userRef, {
                    ...data,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
            } else {
                data = snapshot.data() || {};
            }

            const {
                role,
                clientIds,
                cameraIds,
                locationIds,
            } = readUserAccessFields(data);

            const profile = {
                id: uid,
                ...data,
                role,
                clientIds,
                cameraIds,
                locationIds,
            };

            set({
                profile,
                role,
                clientIds,
                cameraIds,
                locationIds,
                isAccessLoading: false,
                accessError: '',
            });

            return profile;
        } catch (error) {
            console.error('Failed to load user access', error);
            set({
                profile: null,
                role: 'client',
                clientIds: [],
                cameraIds: [],
                locationIds: [],
                isAccessLoading: false,
                accessError: error?.message || 'Unable to load access permissions',
            });
            return null;
        }
    },
    createUser: async (formData) => {
        const { email, password, firstName, lastName, phoneNumber, locationRequested } = formData;
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const { user } = userCredential;

            const trimmedLocationRequested = typeof locationRequested === 'string'
                ? locationRequested.trim()
                : '';

            const userData = {
                email,
                firstName,
                lastName,
                phoneNumber,
                fullName: `${firstName} ${lastName}`.trim(),
                role: 'client',
                clientIds: [],
                cameraIds: [],
                locationIds: [],
                locationRequested: trimmedLocationRequested,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            await setDoc(doc(db, 'users', user.uid), userData, { merge: true });

            return { success: true };
        } catch (err) {
            console.error(err.message);
            return { success: false, error: err?.message || 'Unable to create account' };
        }
    },
    signInEmail: async (email, password) => {
        try {
            const res = await signInWithEmailAndPassword(auth, email, password);
            set({ user: res.user });
            return res;
        } catch (err) {
            console.error(err.message);
            alert(err.message);
            return null;
        }
    },

    signInWithFacebook: async ({ email = '', password = '' } = {}) => {
        const provider = new FacebookAuthProvider();
        FACEBOOK_SIGN_IN_SCOPES.forEach((scope) => provider.addScope(scope));
        provider.setCustomParameters({
            config_id: FACEBOOK_SIGN_IN_CONFIG_ID,
        });

        try {
            const signedInUser = auth.currentUser;
            const response = signedInUser
                ? await linkWithPopup(signedInUser, provider)
                : await signInWithPopup(auth, provider);

            set({ user: response.user });
            return {
                success: true,
                response,
                merged: Boolean(signedInUser),
            };
        } catch (err) {
            if (err?.code === 'auth/account-exists-with-different-credential') {
                const facebookCredential = FacebookAuthProvider.credentialFromError(err);
                const facebookEmail = String(err?.customData?.email || '').trim();
                const enteredEmail = String(email || '').trim();
                const linkingEmail = facebookEmail || enteredEmail;

                if (!facebookCredential || !linkingEmail) {
                    return {
                        success: false,
                        error: 'Facebook found an existing TingVision account, but did not provide enough information to link it safely. Sign in with email first, then connect Facebook from Settings.',
                    };
                }

                if (!enteredEmail || !password) {
                    return {
                        success: false,
                        requiresExistingLogin: true,
                        email: linkingEmail,
                        error: 'This email already has a TingVision account. Enter its existing password, then click “Link Facebook to Existing Account”.',
                    };
                }

                if (enteredEmail.toLowerCase() !== linkingEmail.toLowerCase()) {
                    return {
                        success: false,
                        requiresExistingLogin: true,
                        email: linkingEmail,
                        error: `Facebook uses ${linkingEmail}. Enter that email and its existing TingVision password to link the accounts.`,
                    };
                }

                try {
                    const existingAccount = await signInWithEmailAndPassword(
                        auth,
                        linkingEmail,
                        password,
                    );
                    const linkedAccount = await linkWithCredential(
                        existingAccount.user,
                        facebookCredential,
                    );

                    set({ user: linkedAccount.user });
                    return {
                        success: true,
                        response: linkedAccount,
                        merged: true,
                    };
                } catch (linkError) {
                    if (linkError?.code === 'auth/wrong-password'
                        || linkError?.code === 'auth/invalid-credential') {
                        return {
                            success: false,
                            requiresExistingLogin: true,
                            email: linkingEmail,
                            error: 'That password did not match the existing TingVision account. Please try again.',
                        };
                    }

                    if (linkError?.code === 'auth/credential-already-in-use') {
                        return {
                            success: false,
                            error: 'That Facebook account is already linked to a different TingVision account.',
                        };
                    }

                    console.error(linkError.message);
                    return {
                        success: false,
                        error: linkError?.message || 'TingVision could not link the Facebook account.',
                    };
                }
            }

            if (err?.code === 'auth/credential-already-in-use') {
                return {
                    success: false,
                    error: 'That Facebook account is already linked to a different TingVision account.',
                };
            }

            console.error(err.message);
            return {
                success: false,
                error: err?.message || 'Unable to sign in with Facebook.',
            };
        }
    },
    logout: (e) => {
        try {
            const shouldLogout = window.confirm('Are you sure you want to log out?');
            if (!shouldLogout) {
                return;
            }
            signOut(auth);
            // remove all local storage items
            localStorage.clear();
            set({ ...defaultState, user: null });
        } catch (e) { console.log(e.message); alert(e.message) }
    },
}));

export default useAuthStore;
